// Renders the appropriate value input based on field type (text, numeric, date, dropdown, person, group, boolean).
import React from "react";

import { DatePicker, InputNumeric, InputText, SingleSelectInput } from "@performyard/py-components";

import { RelativeDateInput } from "./RelativeDateInput";

import type { DateSubType, QueryCondition } from "./types";
import { DROPDOWN_TYPES, GROUP_TYPE, NUMERIC_TYPES, PEOPLE_TYPES } from "./types";

import { Single as SelectEmployeeSingle } from "../Inputs/SelectEmployee/Single";
import SelectGroup from "../Inputs/SelectGroup";
import { ANNIVERSARY_COMPARATORS } from "./constants";

type ValueInputProps = {
	fieldType: string;
	dateSubType?: DateSubType;
	comparator: string;
	value: QueryCondition["value"];
	onChange: (value: QueryCondition["value"]) => void;
	fieldOptions?: string[];
	employeeDisplayName?: string;
	index?: number;
};

export const ValueInput = ({
	fieldType,
	dateSubType,
	comparator,
	value,
	onChange,
	fieldOptions = [],
	employeeDisplayName,
	index = 0,
}: ValueInputProps) => {
	if (fieldType === "boolean") {
		const boolValue = value === true ? "true" : value === false ? "false" : undefined;
		return (
			<SingleSelectInput
				options={[
					{ value: "true", label: "True" },
					{ value: "false", label: "False" },
				]}
				value={boolValue}
				onChange={(val) => {
					if (val === "true") onChange(true);
					else if (val === "false") onChange(false);
				}}
				placeholder="Select a value"
				id={`boolean-value-${index}`}
			/>
		);
	}

	if (PEOPLE_TYPES.includes(fieldType)) {
		const oidValue = value && typeof value === "object" && "oid" in value ? value.oid : undefined;
		return (
			<SelectEmployeeSingle
				value={oidValue ? { id: oidValue, name: employeeDisplayName ?? "" } : undefined}
				onChange={(employeeId) => {
					if (employeeId) {
						onChange({ oid: employeeId });
					}
				}}
				placeholder="Search for an employee"
			/>
		);
	}

	if (fieldType === GROUP_TYPE) {
		const oidValue = value && typeof value === "object" && "oid" in value ? value.oid : undefined;
		return (
			<SelectGroup
				value={oidValue}
				onChange={(group) => {
					if (group) {
						onChange({ oid: group.id });
					}
				}}
				placeholder="Select a group"
			/>
		);
	}

	if (NUMERIC_TYPES.includes(fieldType)) {
		const numValue = typeof value === "number" ? value : 0;
		return <InputNumeric value={numValue} onChange={(val) => onChange(val)} placeholder="Enter numeric value" />;
	}

	if (DROPDOWN_TYPES.includes(fieldType)) {
		const options = fieldOptions.map((opt) => ({ value: opt, label: opt }));
		const strValue = typeof value === "string" ? value : undefined;
		return (
			<SingleSelectInput
				options={options}
				value={strValue}
				onChange={(val) => {
					if (val) onChange(val);
				}}
				placeholder="Select a value"
				id={`dropdown-value-${index}`}
			/>
		);
	}

	if (dateSubType === "static") {
		const dateValue = value && typeof value === "object" && "date" in value ? new Date(value.date) : null;
		return (
			<DatePicker
				allowEmpty
				value={dateValue}
				onChange={(date) => {
					if (!date) return;
					onChange({ date: date.toISOString() });
				}}
				placeholder="Select a date"
			/>
		);
	}

	if (dateSubType === "relative" || dateSubType === "anniversary") {
		if (dateSubType === "anniversary" && comparator === ANNIVERSARY_COMPARATORS[0].value) {
			return null;
		}

		let length = 0;
		let unit = "days";

		if (value && typeof value === "object") {
			if ("relative_date" in value) {
				length = Math.abs(value.relative_date);
				unit = value.relative_date_unit;
			} else if ("anniversary_date" in value) {
				length = Math.abs(value.anniversary_date);
				unit = value.anniversary_date_unit;
			}
		}

		return (
			<RelativeDateInput
				length={length}
				unit={unit}
				dateSubType={dateSubType}
				index={index}
				onLengthChange={(newLength) => {
					if (dateSubType === "anniversary") {
						onChange({
							anniversary_date: newLength * -1,
							anniversary_date_unit: unit as "days" | "months",
						});
					} else {
						onChange({
							relative_date: newLength * -1,
							relative_date_unit: unit as "days" | "months" | "years",
						});
					}
				}}
				onUnitChange={(newUnit) => {
					if (dateSubType === "anniversary") {
						onChange({
							anniversary_date: length * -1,
							anniversary_date_unit: newUnit as "days" | "months",
						});
					} else {
						onChange({
							relative_date: length * -1,
							relative_date_unit: newUnit as "days" | "months" | "years",
						});
					}
				}}
			/>
		);
	}

	// Default: text input
	const strValue = typeof value === "string" ? value : "";
	return <InputText value={strValue} onChange={(val) => onChange(val)} placeholder="Enter a value" />;
};
